import loadImage from 'blueimp-load-image';
import snakeCase from 'lodash/snakeCase';

export default class Exif {
  constructor(file = null, localize = {}) {
    this.file = file;
    this.localize = localize;
  }

  async storeExif() {
    const input = document.querySelector('[data-image-attributes]');
    const store = {};
    const fields = [
      'Model',
      'LensMake',
      'LensModel',
      'ShutterSpeedValue',
      'ApertureValue',
      'FocalLength',
      'MeteringMode',
      'ExposureMode',
      'PhotographicSensitivity',
      'WhiteBalance',
      'CompressedBitsPerPixel',
    ];

    if (this.file === null) {
      console.error('file is not found');
      return;
    }

    const data = await loadImage(this.file, {
      meta: true,
    });
    const exif = data?.exif?.getAll();

    if (!exif) {
      return {};
    }

    fields.forEach((key) => {
      const snakeKey = snakeCase(key);
      if (key === 'Model') {
        store[snakeKey] = exif[key];
        return;
      }
      store[snakeKey] = exif.Exif[key];
    });

    const storeCompact = Object.fromEntries(
      Object.entries(store).filter(([key]) => !!store[key])
    );

    input.value = JSON.stringify(storeCompact);
    return storeCompact;
  }

  addAccordionAria(target) {
    if (target === null) return;
    const accordion = document.querySelector(
      target.dataset.accordionTarget
    )

    const elements = [target, target.querySelector('svg'), accordion]
    const isMedium = window.matchMedia('(min-width: 769px)').matches
    const isEditable = ['edit', 'new'].includes(target.dataset.accordionActionName)

    // transitionを無効にする
    elements.forEach((element) => {
      element.style.transition = 'none';
    })


    if (isMedium || isEditable) {
      target.setAttribute('aria-expanded', true);
      accordion.setAttribute('aria-hidden', false);
    }

    // transitionを有効にする
    setTimeout(() => {
      elements.forEach((element) => {
        element.style.visibility = '';
        element.style.transition = '';
      })
      // 400ms は css transition の時間
    }, 400);
  }

  addAccordionEvent(target) {
    if (target === null) return;
    const accordion = document.querySelector(
      target.dataset.accordionTarget
    );

    target.addEventListener('click', () => {
      if (accordion === null) {
        console.error('accordion target is not found');
        return;
      }

      const isExpanded = target.getAttribute('aria-expanded') === 'true';
      target.setAttribute('aria-expanded', !isExpanded);
      accordion.setAttribute('aria-hidden', isExpanded);
    });
  }

  async createPreviewRender(preview = '.js-exif-preview') {
    const store = await this.storeExif();
    const exifRender = document.querySelector(preview);

    if (exifRender === null) {
      console.error('preview is not found');
      return;
    }

    if (Object.keys(store).length < 1) {
      document.querySelector(exifRender.dataset.exifAttributeRender)?.remove();
      return;
    }

    if (exifRender.childElementCount > 0) {
      exifRender.replaceChildren();
    }

    const iconUrl = exifRender.dataset.exifIconUrl;
    const { component, button } = await this.createRenderComponent(
      store,
      iconUrl
    );

    exifRender.insertAdjacentElement('afterbegin', component);
    ['addAccordionAria', 'addAccordionEvent'].forEach((fn) => this[fn](button))
  }

  async createRenderComponent(store = {}, url = '') {
    const svg = await this.createRenderSVG(url);
    const renderSet = {
      component: {
        element: 'div',
        class: ['c-ImageAttribute'],
      },
      headline: {
        element: 'h2',
        class: ['c-ImageAttribute_Label'],
      },
      button: {
        element: 'button',
        type: 'button',
        class: ['c-ImageAttribute_Button', 'js-image-attribute-accordion'],
        data: {
          accordionTarget: '.c-ImageAttribute_List',
          accordionActionName: 'edit',
        },
        text: 'Exif（アイキャッチ画像）',
        ariaExpanded: true,
      },
      icon: {
        element: 'i',
      },
      list: {
        element: 'div',
        class: ['c-ImageAttribute_List'],
        ariaHidden: false,
      },
    };

    const [component, headline, button, icon, list] = Object.keys(
      renderSet
    ).map((key) => {
      const element = document.createElement(renderSet[key].element);

      if (renderSet[key].class) {
        element.classList.add(...renderSet[key].class);
      }

      if (renderSet[key].text) {
        element.textContent = renderSet[key].text;
      }

      if (renderSet[key].type) {
        element.type = renderSet[key].type;
      }

      if (renderSet[key].data) {
        Object.keys(renderSet[key].data).map((dataKey) => {
          element.dataset[dataKey] = renderSet[key].data[dataKey];
        });
      }

      if ('ariaExpanded' in renderSet[key]) {
        element.setAttribute("aria-expanded", renderSet[key].ariaExpanded);
      }

      if ('ariaHidden' in renderSet[key]) {
        element.setAttribute("aria-hidden", renderSet[key].ariaHidden);
      }

      return element;
    });

    Object.keys(store).forEach((key) => {
      list.append(this.createRenderItem(key, store[key]));
    });

    icon.insertAdjacentHTML('afterbegin', svg);
    button.append(icon);
    headline.append(button);
    component.append(headline);
    component.append(list);
    return {
      component,
      button,
    };
  }

  createRenderItem(typeContent, bodyContent) {
    const itemSet = {
      item: {
        element: 'dl',
        class: ['c-ImageAttribute_Item'],
      },
      type: {
        element: 'dt',
        class: ['c-ImageAttribute_Type'],
        text: typeContent,
      },
      body: {
        element: 'dd',
        class: ['c-ImageAttribute_Body'],
        text: bodyContent,
      },
    };

    const [dl, dt, dd] = Object.keys(itemSet).map((key) => {
      const element = document.createElement(itemSet[key].element);
      if (itemSet[key].class) {
        element.classList.add(...itemSet[key].class);
      }

      if (itemSet[key].text) {
        if (key === 'type') {
          element.textContent = this.localize[typeContent];
        }

        if (key === 'body') {
          element.textContent = itemSet[key].text;
        }
      }

      return element;
    });

    dl.append(dt);
    dl.append(dd);
    return dl;
  }

  async createRenderSVG(svgPath) {
    return await fetch(svgPath).then((response) => response.text());
  }
}
